import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const clamp = (value: number): number => Math.max(0, Math.min(value, 100));

export const MetricBar = ({
  label,
  value,
  className
}: {
  label: string;
  value: number;
  className?: string;
}) => {
  const reducedMotion = useReducedMotion();
  const mobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 960px)').matches;
  const normalized = clamp(value);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between text-xs">
        <span className="hud-micro text-brand-text-3">{label}</span>
        <motion.span
          className="font-mono text-brand-lime"
          animate={reducedMotion ? undefined : { opacity: [0.55, 1, 0.65] }}
          transition={{ duration: 1.4, repeat: Infinity }}
        >
          {normalized}%
        </motion.span>
      </div>
      <div className="relative h-3 overflow-hidden rounded-full border border-brand-lime/25 bg-brand-panel-2">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-[linear-gradient(90deg,rgba(139,255,62,0.85),rgba(88,247,182,0.9))]"
          initial={mobileViewport ? false : { width: '0%' }}
          animate={mobileViewport ? { width: `${normalized}%` } : undefined}
          whileInView={mobileViewport ? undefined : { width: `${normalized}%` }}
          viewport={mobileViewport ? undefined : { once: true, amount: 0.8 }}
          transition={{ duration: 0.9, ease: [0.2, 1, 0.32, 1] }}
        />
        <motion.div
          className="absolute inset-y-0 w-16 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)]"
          animate={reducedMotion ? undefined : { x: ['-120%', '420%'] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'linear' }}
        />
        <motion.span
          className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand-lime/60 bg-brand-bg"
          style={{ left: `${normalized}%` }}
          animate={reducedMotion ? undefined : { boxShadow: ['0 0 0 rgba(183,255,106,0)', '0 0 12px rgba(183,255,106,0.6)', '0 0 0 rgba(183,255,106,0)'] }}
          transition={{ duration: 1.9, repeat: Infinity }}
        />
      </div>
    </div>
  );
};
